import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Merge, Check } from "lucide-react";
import { useConfirm } from "@/hooks/useConfirm";

// Admin utility: scans expense + vendor rows for near-duplicate business names
// (Home Depot / HomeDepot / home-depot!) and offers a one-tap merge into the
// preferred spelling.
const BusinessDuplicates = ({ onChanged }) => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chosen, setChosen] = useState({}); // key -> preferred name string
  const [busyKey, setBusyKey] = useState(null);
  const confirm = useConfirm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/expenses/businesses/duplicates");
      setGroups(data || []);
      const next = {};
      (data || []).forEach((g) => { next[g.key] = g.suggested; });
      setChosen(next);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load duplicates");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const runMerge = async (group) => {
    const keep = chosen[group.key];
    const merge = group.variants.map((v) => v.name).filter((n) => n !== keep);
    if (!merge.length) return;
    const ok = await confirm({
      title: `Merge into "${keep}"?`,
      description: `${merge.length} variant(s) will be rewritten: ${merge.map((m) => `"${m}"`).join(", ")}.`,
      confirmLabel: "Merge",
    });
    if (!ok) return;
    setBusyKey(group.key);
    try {
      const { data } = await api.post("/expenses/businesses/merge", { keep, merge });
      toast.success(`Merged · ${data.expenses_updated} expense(s) updated`);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Merge failed");
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div className="mt-6 rounded-lg border border-white/10 bg-white/[0.02] p-4" data-testid="business-duplicates">
      <div className="mb-3 flex items-center gap-2">
        <Merge className="h-4 w-4 text-[#4f6fe8]" />
        <h3 className="font-mono text-[10px] uppercase tracking-widest text-white/50">
          Business duplicates{groups.length ? ` · ${groups.length} group(s)` : ""}
        </h3>
      </div>
      {loading ? (
        <p className="font-mono text-xs text-white/40">Scanning…</p>
      ) : groups.length === 0 ? (
        <div className="flex items-center gap-2 rounded-md border border-emerald-500/25 bg-emerald-500/5 px-3 py-2 text-xs text-emerald-200">
          <Check className="h-3.5 w-3.5" /> No duplicate businesses detected.
        </div>
      ) : (
        <ul className="space-y-3">
          {groups.map((g) => (
            <li key={g.key} data-testid={`dup-group-${g.key}`} className="rounded-md border border-white/10 bg-white/[0.02] p-3">
              <p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-white/50">
                Keep as:
              </p>
              <div className="mb-3 flex flex-wrap gap-1.5">
                {g.variants.map((v) => (
                  <button
                    key={v.name}
                    type="button"
                    onClick={() => setChosen((s) => ({ ...s, [g.key]: v.name }))}
                    data-testid={`dup-variant-${g.key}-${v.name}`}
                    className={`rounded-md border px-2.5 py-1 text-xs transition-colors ${
                      chosen[g.key] === v.name
                        ? "border-emerald-400 bg-emerald-500/15 text-emerald-100"
                        : "border-white/15 bg-white/[0.03] text-white/80 hover:border-white/25"
                    }`}
                  >
                    <span className="font-semibold">{v.name}</span>
                    <span className="ml-1.5 font-mono text-[10px] text-white/40">×{v.count}</span>
                  </button>
                ))}
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => runMerge(g)}
                  disabled={busyKey === g.key}
                  data-testid={`dup-merge-${g.key}`}
                  className="rounded-md bg-[#1a3fd1] px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-white hover:bg-[#1a3fd1]/85 disabled:opacity-60"
                >
                  <Merge className="mr-1.5 inline h-3.5 w-3.5" />
                  {busyKey === g.key ? "Merging…" : `Merge ${g.variants.length - 1} into "${chosen[g.key] || g.suggested}"`}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default BusinessDuplicates;
